import {
  ADDRESS_FIELDS,
  EMAIL_FORMAT_REGEX,
  NAME_REGEX,
  PHONE_REGEX,
  SOLUTION_CODE_DEVICE_PAGE,
  SOLUTION_CODE_QR_CODE,
} from "./ticketFormConfig";

// A stable React key for each attached image, so deleting one doesn't shift the others' keys.
// Kept per File object, so the same file picked twice still gets two different keys.
const imageKeys = new WeakMap();
let nextImageKey = 0;
export const getImageKey = (image) => {
  if (!imageKeys.has(image)) {
    nextImageKey += 1;
    imageKeys.set(image, `image-${nextImageKey}`);
  }
  return imageKeys.get(image);
};

// Why a value typed into a field isn't allowed, or "" when it is.
// allowed: "letters" (names, address text), "digits" (phones, address numbers), or undefined for any text (emails).
export const getFieldInputError = (value, label, maxLength, allowed) => {
  if (value.length > maxLength) {
    return `ניתן להזין עד ${maxLength} תווים בשדה ${label}`;
  }
  if (allowed === "letters" && !NAME_REGEX.test(value)) {
    return `שדה ${label} יכול להכיל אותיות בלבד`;
  }
  if (allowed === "digits" && !PHONE_REGEX.test(value)) {
    return `שדה ${label} יכול להכיל מספרים בלבד`;
  }
  return "";
};

// "שדה חובה" for each required address field the customer left empty
export const getMissingAddressFields = (addressChangeRequest) => {
  const missingRequiredErrors = {};
  ADDRESS_FIELDS.forEach(({ key, required }) => {
    if (required && !addressChangeRequest[key].trim()) {
      missingRequiredErrors[key] = "שדה חובה";
    }
  });
  return missingRequiredErrors;
};

// "שדה חובה" for each contact person field the customer left empty
export const getMissingContactFields = ({ contactNameChangeRequest, contactPhoneChangeRequest, contactEmailChangeRequest }) => {
  const missingRequiredErrors = {};
  if (!contactNameChangeRequest.trim()) {
    missingRequiredErrors.contactName = "שדה חובה";
  }
  if (!contactPhoneChangeRequest.trim()) {
    missingRequiredErrors.contactPhone = "שדה חובה";
  }
  if (!contactEmailChangeRequest.trim()) {
    missingRequiredErrors.contactEmail = "שדה חובה";
  }
  return missingRequiredErrors;
};

// The ticket summary shown on the success page
export const buildSuccessPageState = ({ ticketData, formData, data, response }) => ({
  state: {
    ticketInfo: {
      name: ticketData.updateSource,
      type: formData.callType,
      serialNumber: data.SERNUM,
      urgency: formData.urgency,
      ticketResponse: response.data,
    },
  },
});

// Checks the form before it's sent, in the order the customer sees the messages.
// Returns the message to show, or null when the form is ready to send.
export const getTicketFormError = ({ formData, addressChangeRequest, contactNameChangeRequest, contactPhoneChangeRequest }) => {
  if (
    !formData.firstName ||
    !formData.lastName ||
    !formData.phone ||
    !formData.text ||
    !formData.callType ||
    !formData.urgency ||
    !formData.email
  ) {
    return "אנא מלא את כל השדות הנדרשים";
  }

  if (!EMAIL_FORMAT_REGEX.test(formData.email)) {
    return "שדה אימייל יכול להכיל כתובת מייל תקינה בלבד";
  }

  const addressHasValue = ADDRESS_FIELDS.some(
    ({ key }) => addressChangeRequest[key].trim()
  );

  if (!formData.confirmAddress && !addressHasValue) {
    return "יש לאשר שכתובת האתר נכונה או לעדכן אותה";
  }

  if (
    !formData.confirmContactPerson &&
    !(contactNameChangeRequest.trim() && contactPhoneChangeRequest.trim())
  ) {
    return "יש לאשר שאיש הקשר נכון או לעדכן אותו";
  }

  if (!formData.termsAccepted) {
    return "יש לאשר את תנאי השימוש ומדיניות הפרטיות כדי להמשיך";
  }

  return null;
};

// Builds the data sent to createTicket from the form state.
// Address and contact change requests are sent only when the customer didn't confirm the current details.
export const buildTicketData = ({
  data,
  formData,
  openedViaDevice,
  addressChangeRequest,
  contactNameChangeRequest,
  contactPhoneChangeRequest,
  contactEmailChangeRequest,
}) => {
  const fullName = `${formData.firstName} ${formData.lastName}`.trim();
  const formattedAddressChangeRequest = ADDRESS_FIELDS.filter(
    ({ key }) => addressChangeRequest[key].trim()
  )
    .map(({ key, label }) => `${label}: ${addressChangeRequest[key].trim()}`)
    .join(", ");
  return {
    serialNumber: data.SERNUM,
    customerNumber: data.CUSTNAME,
    callType: formData.callType,
    updateSource: fullName,
    contactInfo: formData.phone,
    description: formData.text,
    urgency: formData.urgency,
    email: formData.email,
    solutionCode: openedViaDevice ? SOLUTION_CODE_DEVICE_PAGE : SOLUTION_CODE_QR_CODE,
    images: formData.images,
    addressChangeRequest: formData.confirmAddress ? "" : formattedAddressChangeRequest,
    contactNameChangeRequest: formData.confirmContactPerson ? "" : contactNameChangeRequest,
    contactPhoneChangeRequest: formData.confirmContactPerson ? "" : contactPhoneChangeRequest,
    contactEmailChangeRequest: formData.confirmContactPerson ? "" : contactEmailChangeRequest,
  };
};
